/* eslint-disable jsx-a11y/alt-text */
import {Page, View, Text, Image, Document} from '@react-pdf/renderer'
import moment from 'moment'
import styles from './invoiceStyle'
import {ToWords} from 'to-words'
import {toAbsoluteUrl} from '../../../../../_metronic/helpers'
import {COMPANY_INFO} from '../companyInfo'

/** Inserts newline breaks — react-pdf often will not wrap long tokens inside narrow flex columns. */
function isCancelledBillableStatus(status: unknown): boolean {
  return String(status ?? '').toLowerCase() === 'cancelled-billable'
}

const apptColumnStyle = {
  flexBasis: '4%',
  paddingTop: 2,
  paddingBottom: 2,
  paddingLeft: 2,
  paddingRight: 14,
} as const

const requesterColumnStyle = {
  flexBasis: '10%',
  minWidth: 0,
  flexShrink: 1,
  paddingTop: 2,
  paddingBottom: 2,
  paddingLeft: 14,
  paddingRight: 2,
} as const

function formatRequesterForPdf(raw: unknown, lineMaxChars = 14): string {
  const s = raw == null || raw === '' ? 'N/A' : String(raw)
  if (s === 'N/A') return s

  const chunk = (part: string): string[] => {
    const lines: string[] = []
    for (let i = 0; i < part.length; i += lineMaxChars) {
      lines.push(part.slice(i, i + lineMaxChars))
    }
    return lines
  }

  if (s.includes('.')) {
    const segs = s.split('.')
    const lines: string[] = []
    for (let i = 0; i < segs.length; i++) {
      const piece = segs[i] + (i < segs.length - 1 ? '.' : '')
      if (!piece) continue
      lines.push(...chunk(piece))
    }
    return lines.join('\n')
  }

  return chunk(s).join('\n')
}

// ----------------------------------------------------------------------
type Props = {
  items?: any
  details?: any
  invoiceDetails?: any
}

const InvoicePdf: React.FC<Props> = ({items, details, invoiceDetails}) => {
  const toWords = new ToWords()

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <View>
          <View fixed>
            <View style={[styles.gridContainer, styles.mb3]}>
              <View style={{alignItems: 'flex-start', flexDirection: 'column'}}>
                <Text style={[styles.font10, styles.subtitle2, {color: '#000'}]}>
                  {COMPANY_INFO.name}
                </Text>
                <Text style={[styles.font10, {color: '#000'}]}>{COMPANY_INFO.address}</Text>
              </View>
              <View style={{alignItems: 'flex-start'}}>
                <Image
                  source={toAbsoluteUrl('/media/logos/Untitled-1.png')}
                  style={[styles.logo, {width: 50, height: 50}]}
                />
              </View>
            </View>
            <View style={[styles.gridContainer, styles.mb8, styles.redbg]}>
              <View style={[styles.whiteContainer]}>
                <Text style={[styles.whitebg]}>INVOICE | COPY</Text>
              </View>
            </View>
          </View>
          <View style={[styles.borderstyle, styles.uppercase]}>
            
            <View style={[styles.gridContainer, styles.mb3, styles.font10, {color: '#000'}]}>
              {/* Left side: "To" Address */}
              <View style={[styles.gridContainer, styles.col7, styles.mr3]}>
                <View style={styles.col12}>
                  <Text style={{color: '#000'}}>To:</Text>
                  <Text style={[styles.font10, styles.subtitle2, {color: '#000'}]}>{invoiceDetails?.customer}</Text>
                  {invoiceDetails?.customerAddress &&
                    invoiceDetails?.customerAddress.split(',').map((item: any, index: number) => (
                      <Text style={[styles.font10, {color: '#000'}]} key={index}>
                        {item}
                        {invoiceDetails?.customerAddress?.split(',').length !== index + 1
                          ? ','
                          : '.'}
                      </Text>
                    ))}
                </View>
              </View>

              {/* Right side: Invoice Info */}
              <View
                style={[
                  styles.gridContainer,
                  styles.col5,
                  {justifyContent: 'flex-start', alignItems: 'stretch', textAlign: 'right'}, // Center vertically and align right
                ]}
              >
                <View style={styles.col12}>
                  <Text style={{color: '#000'}}>Invoice #: {invoiceDetails?.invoiceNo ?? 'N/A'}</Text>
                  <Text style={{color: '#000'}}>
                    Invoice Date: {moment(invoiceDetails?.invoiceDate)?.format('YYYY-MM-DD')}
                  </Text>
                  <Text style={{color: '#000'}}>Due Date: {moment(invoiceDetails?.dueDate)?.format('YYYY-MM-DD')}</Text>
                  <Text style={{color: '#000'}}>Time Zone: {invoiceDetails?.timeZone ?? 'N/A'}</Text>
                </View>
              </View>
            </View>
            <View
              style={{
                flexDirection: 'row',
                marginTop: 8,
                marginBottom: 8,
                paddingHorizontal: 8,
                paddingVertical: 5,
                backgroundColor: '#f8f9fa', 
                borderWidth: 1, 
                borderColor: '#d1d5db', 
                borderRadius: 8,
              }}
            >
              <Text style={{color: '#000', fontWeight: 'bold'}}>
                {' '}
                {/* equivalent to text-dark fw-bold */}
                Line Count: {invoiceDetails?.invoiceLineList?.length ?? 0}
              </Text>
            </View>

            {/* Improved full-width table for invoice lines, with better column sizing and text wrapping */}
            <View style={{borderWidth: 1, borderColor: '#ccc', marginBottom: 10, width: '100%'}}>
              <View style={{padding: 0}}>
                {/* Table header */}
                <View style={{flexDirection: 'row', borderBottomWidth: 1, borderColor: '#ddd', backgroundColor: '#f8f9fa'}}>
                  <View style={{flexBasis: '16%', padding: 2}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Call</Text></View>
                  <View style={apptColumnStyle}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Appt</Text></View>
                  <View style={requesterColumnStyle}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Requester</Text></View>
                  <View style={{flexBasis: '4%', paddingTop: 2, paddingBottom: 2, paddingLeft: 2, paddingRight: 6}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Type</Text></View>
                  <View style={{flexBasis: '10%', paddingTop: 2, paddingBottom: 2, paddingLeft: 6, paddingRight: 2}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Date</Text></View>
                  <View style={{flexBasis: '7%', padding: 2}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Start</Text></View>
                  <View style={{flexBasis: '7%', padding: 2}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>End</Text></View>
                  <View style={{flexBasis: '6%', padding: 2}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Dur.</Text></View>
                  <View style={{flexBasis: '6%', padding: 2}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Rate</Text></View>
                  <View style={{flexBasis: '6%', padding: 2}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Extra</Text></View>
                  <View style={{flexBasis: '7%', padding: 2}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Miles</Text></View>
                  <View style={{flexBasis: '7%', padding: 2}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Travel</Text></View>
                  <View style={{flexBasis: '10%', padding: 2}}><Text style={{fontWeight: 'bold', fontSize: 9, color: '#000'}}>Total</Text></View>
                </View>
                {/* Table body */}
                {invoiceDetails?.invoiceLineList?.map((item: any, index: number) => {
                  let endTime = '';
                  try {
                    endTime = item?.endTime
                      ? moment(item.endTime).format('hh:mm A')
                      : item?.callTime && item?.duration
                        ? moment(item.callTime).add(item.duration, 'minutes').format('hh:mm A')
                        : 'N/A';
                  } catch (e) { endTime = 'N/A'; }
                  return (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        borderBottomWidth: 1,
                        borderColor: '#eee',
                        backgroundColor: item?.rate === 0 ? '#ffd1d7' : undefined,
                      }}
                      key={index}
                    >
                      <View style={{flexBasis: '16%', padding: 2}}>
                        <Text style={{fontSize: 9, color: '#000'}} wrap>{item?.contactId ?? 'N/A'}</Text>
                        <Text style={{fontSize: 8, color: '#000'}} wrap>
                          {item?.fromLanguage ?? 'N/A'} to {item?.toLanguage ?? 'N/A'}
                        </Text>
                        <Text style={{fontSize: 8, color: '#000'}} wrap>
                          RecordId: {item?.recordId ?? 'N/A'}
                        </Text>
                        {item?.location && (
                          <Text style={{fontSize: 8, color: '#000'}} wrap>
                           Location: {item.location}
                          </Text>
                        )}
                        {item?.consumer && (
                          <Text style={{fontSize: 8, color: '#000'}} wrap>
                            Consumer: {item.consumer}
                          </Text>
                        )}
                        {item?.interpreterName && (
                          <Text style={{fontSize: 8, color: '#000'}} wrap>
                            Interpreter: {item.interpreterName}
                          </Text>
                        )}
                        {item?.assignmentStatus && (
                          <Text
                            style={{
                              fontSize: 8,
                              color: isCancelledBillableStatus(item.assignmentStatus) ? 'red' : '#000',
                              fontWeight: isCancelledBillableStatus(item.assignmentStatus) ? 'bold' : 'normal',
                            }}
                            wrap
                          >
                            {item.assignmentStatus}
                          </Text>
                        )}
                      </View>
                      <View style={apptColumnStyle}>
                        <Text style={{fontSize: 9, color: '#000'}} wrap>{item?.appointmentId ?? 'N/A'}</Text>
                      </View>
                      <View style={requesterColumnStyle}>
                        <Text style={{fontSize: 9, color: '#000'}} wrap>
                          {formatRequesterForPdf(item?.requester)}
                        </Text>
                      </View>
                      <View style={{flexBasis: '4%', paddingTop: 2, paddingBottom: 2, paddingLeft: 2, paddingRight: 6}}>
                        <Text style={{fontSize: 9, color: '#000'}} wrap>{item?.callType ?? 'N/A'}</Text>
                      </View>
                      <View style={{flexBasis: '10%', paddingTop: 2, paddingBottom: 2, paddingLeft: 6, paddingRight: 2}}>
                        <Text style={{fontSize: 9, color: '#000'}}>{item?.appointmentDate ?? item?.callTime ? moment(item.appointmentDate ?? item.callTime).format('YYYY-MM-DD') : 'N/A'}</Text>
                      </View>
                      <View style={{flexBasis: '7%', padding: 2}}>
                        <Text style={{fontSize: 9, color: '#000'}}>{item?.callTime ? moment(item.callTime).format('hh:mm A') : 'N/A'}</Text>
                      </View>
                      <View style={{flexBasis: '7%', padding: 2}}>
                        <Text style={{fontSize: 9, color: '#000'}}>{endTime}</Text>
                      </View>
                      <View style={{flexBasis: '6%', padding: 2}}>
                        <Text style={{fontSize: 9, color: '#000'}}>{item?.durationDisplay || (item?.duration ? `${item.duration} min` : 'N/A')}</Text>
                      </View>
                      <View style={{flexBasis: '6%', padding: 2}}>
                        <Text style={{fontSize: 9, color: '#000'}}>${item?.totalBillingMinutes > 0 ? item?.rate : 0}</Text>
                      </View>
                      <View style={{flexBasis: '6%', padding: 2}}>
                        <Text style={{fontSize: 9, color: '#000'}}>${item?.extra ?? 0}</Text>
                      </View>
                      <View style={{flexBasis: '7%', padding: 2}}>
                        <Text style={{fontSize: 9, color: '#000'}}>${item?.milageExpense ?? 0}</Text>
                      </View>
                      <View style={{flexBasis: '7%', padding: 2}}>
                        <Text style={{fontSize: 9, color: '#000'}}>${item?.travelCharges ?? 0}</Text>
                      </View>
                      <View style={{flexBasis: '10%', padding: 2}}>
                        <Text style={{fontSize: 9, color: '#000'}}>${item?.total ?? 0}</Text>
                      </View>
                    </View>
                  );
                })}
                {/* Table footer: TOTAL row */}
                <View style={{flexDirection: 'row', borderTopWidth: 2, borderColor: '#222', marginTop: 8}}>
                  <View style={{flex: 1, alignItems: 'flex-end', padding: 6}}>
                    <Text style={{fontWeight: 'bold', color: 'red', fontSize: 10}}>(*Taxes Not Included)</Text>
                  </View>
                  <View style={{flex: 1, alignItems: 'flex-end', padding: 6}}>
                    <Text style={{fontWeight: 'bold', fontSize: 12}}>
                      TOTAL: $
                      {invoiceDetails?.invoiceLineList?.reduce((acc: number, item: any) => acc + (+item?.total || 0), 0).toFixed(2)}
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            <View style={[styles.gridContainer, styles.mb3, styles.mt5]}>
              
              <View style={[styles.col4]}>
                <View style={(styles.col12, styles.signatureStyle)}>
                  {' '}
                  <Text>Adastra</Text>
                  <View>
                    <Image
                      source={toAbsoluteUrl('/media/logos/signature.png')}
                      style={[styles.signature]}
                    />
                  </View>
                  <Text style={[styles.hr, styles.mt3]}></Text>
                  <Text>Authorized Signature</Text>
                </View>
              </View>
            </View>
          </View>
        </View>
        <View fixed>
          <View>
            {/* <View style={styles.col6}>
              <Text>
                <Text style={styles.subtitle2}>CREATED BY :</Text> {'N/A'}
              </Text>
            </View> */}
            {/* <View style={[styles.col6, styles.alignRight]}>
              <Text>
                <Text style={styles.subtitle2}>H</Text> +94 11 481 3921 | +94 11 7474747 |{' '}
                <Text style={styles.subtitle2}>F</Text> +94 11 481 3920
              </Text>
              <Text style={styles.lowercase}>hello@adastra.lk | www.adastra.lk</Text>
            </View> */}
          </View>
        </View>
      </Page>
    </Document>
  )
}

export {InvoicePdf}
